import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api, { apiErr } from "../lib/api";
import { confColor } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import CandleChart from "./CandleChart";
import { TrendingUp, TrendingDown, X, ShieldCheck, ShieldAlert, Loader2, Sparkles } from "lucide-react";

function Row({ label, value, valueClass = "" }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">{label}</span>
      <span className={`text-sm font-mono tabular ${valueClass}`}>{value}</span>
    </div>
  );
}

export default function TradeCard({ signal, onExecuted, index = 0 }) {
  const { t } = useLang();
  const isBuy = signal.direction === "BUY";
  const [open, setOpen] = useState(false);
  const [ignored, setIgnored] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  if (ignored) return null;
  const cc = confColor(signal.confidence);

  const openDialog = async () => {
    setOpen(true);
    setPreview(null);
    try {
      const { data } = await api.post("/trades/preview", body());
      setPreview(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const body = () => ({
    symbol: signal.symbol, direction: signal.direction, entry: signal.entry,
    stop_loss: signal.stop_loss, take_profit_1: signal.take_profit_1,
    take_profit_2: signal.take_profit_2, strategy: signal.strategy, confidence: signal.confidence,
  });

  const execute = async () => {
    setBusy(true);
    try {
      await api.post("/trades/execute", body());
      toast.success(`${signal.symbol} ${signal.direction} — ${t("execute")} ✓`);
      setOpen(false);
      onExecuted?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div data-testid={`trade-card-${signal.symbol.replace("/", "-")}`}
        className="fade-up border border-[#1E2028] bg-[#0F1015] hover:border-[#4B5563] transition-colors duration-200"
        style={{ borderTop: `2px solid ${cc}`, animationDelay: `${index * 40}ms` }}>
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-display font-bold text-base">{signal.symbol}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">{signal.asset_class}</div>
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 text-xs font-bold ${isBuy ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
              {isBuy ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
              {signal.direction}
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[#9CA3AF] font-mono">{t("confidence")}</span>
              <span className="font-mono font-semibold" style={{ color: cc }}>{signal.confidence}/100</span>
            </div>
            <div className="h-1.5 bg-[#05050A] overflow-hidden">
              <div className="h-full transition-[width] duration-500" style={{ width: `${signal.confidence}%`, background: cc }} />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-3">
            <Row label={t("entry")} value={signal.entry} />
            <Row label={t("stopLoss")} value={signal.stop_loss} valueClass="text-[#EF4444]" />
            <Row label="TP1" value={signal.take_profit_1} valueClass="text-[#10B981]" />
            <Row label="TP2" value={signal.take_profit_2} valueClass="text-[#10B981]" />
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-[#1E2028] pt-3 text-xs">
            <span className="text-[#FACC15] font-mono">{signal.strategy}</span>
            <span className="text-[#9CA3AF] font-mono">R/R 1:{signal.risk_reward}</span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2">
            <button data-testid={`execute-${signal.symbol.replace("/", "-")}`} onClick={openDialog}
              className="bg-[#FACC15] text-[#05050A] font-semibold text-xs py-2 hover:bg-[#eab308] transition-colors duration-200">
              {t("execute")}
            </button>
            <button data-testid={`ignore-${signal.symbol.replace("/", "-")}`} onClick={() => setIgnored(true)}
              className="border border-[#1E2028] text-[#9CA3AF] text-xs py-2 hover:border-[#EF4444] hover:text-[#EF4444] transition-colors duration-200">
              {t("ignore")}
            </button>
            <button data-testid={`details-${signal.symbol.replace("/", "-")}`} onClick={openDialog}
              className="border border-[#1E2028] text-[#9CA3AF] text-xs py-2 hover:border-[#4B5563] hover:text-white transition-colors duration-200">
              {t("details")}
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1015] border-[#1E2028] text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="trade-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {signal.symbol}
              <span className={`text-xs px-2 py-0.5 ${isBuy ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>{signal.direction}</span>
              <span className="text-xs font-mono" style={{ color: cc }}>{signal.confidence}/100</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div className="border border-[#1E2028] p-2">
              <CandleChart symbol={signal.symbol} entry={signal.entry} stop_loss={signal.stop_loss}
                take_profit_1={signal.take_profit_1} take_profit_2={signal.take_profit_2} />
            </div>

            <div className="flex items-start gap-2 bg-[#FACC15]/5 border border-[#FACC15]/20 p-3">
              <Sparkles className="w-4 h-4 text-[#FACC15] shrink-0 mt-0.5" />
              <p className="text-[#D1D5DB] text-xs leading-relaxed">{signal.explanation}</p>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono mb-1.5">{t("reasons")}</div>
              <ul className="space-y-1">
                {signal.reasons?.map((r, i) => (
                  <li key={i} className="text-xs text-[#9CA3AF] flex items-center gap-2">
                    <span className="w-1 h-1 bg-[#FACC15]" /> {r}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-3 gap-3 border-t border-[#1E2028] pt-3">
              <Row label={t("trend")} value={signal.trend} />
              <Row label={t("volatility")} value={signal.volatility} />
              <Row label={t("risk")} value={signal.risk} />
              <Row label={t("riskReward")} value={`1:${signal.risk_reward}`} />
              <Row label="ATR" value={signal.atr} />
              <Row label="Spread" value={signal.spread} />
            </div>

            {/* sizing + guardian */}
            <div className="border-t border-[#1E2028] pt-3">
              {!preview ? (
                <div className="flex items-center gap-2 text-xs text-[#6B7280]"><Loader2 className="w-4 h-4 animate-spin" /> {t("positionSizing")}…</div>
              ) : (
                <>
                  {preview.sizing.ok ? (
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <Row label={t("lots")} value={preview.sizing.lots} />
                      <Row label={t("units")} value={preview.sizing.units} />
                      <Row label={t("notional")} value={`€${preview.sizing.notional}`} />
                      <Row label={t("riskAmount")} value={`€${preview.sizing.risk_amount} (${preview.sizing.effective_risk_pct}%)`} valueClass="text-[#F59E0B]" />
                    </div>
                  ) : (
                    <div className="mb-3 text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 p-2">{preview.sizing.reason}</div>
                  )}
                  <div className={`flex items-start gap-2 p-2 text-xs border ${preview.guardian.allowed ? "border-[#10B981]/30 bg-[#10B981]/10" : "border-[#EF4444]/30 bg-[#EF4444]/10"}`}>
                    {preview.guardian.allowed ? <ShieldCheck className="w-4 h-4 text-[#10B981] shrink-0" /> : <ShieldAlert className="w-4 h-4 text-[#EF4444] shrink-0" />}
                    <div>
                      <div className={preview.guardian.allowed ? "text-[#10B981] font-semibold" : "text-[#EF4444] font-semibold"}>
                        {t("riskGuardian")}: {preview.guardian.allowed ? t("allowed") : t("blocked")}
                      </div>
                      <ul className="mt-1 text-[#9CA3AF] space-y-0.5">
                        {preview.guardian.reasons.map((r, i) => <li key={i}>· {r}</li>)}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button data-testid="confirm-trade" onClick={execute}
              disabled={busy || !preview || !preview.sizing.ok || !preview.guardian.allowed}
              className="w-full bg-[#FACC15] text-[#05050A] font-bold py-3 text-sm uppercase tracking-wide hover:bg-[#eab308] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {t("confirmTrade")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
